import { useCallback, useMemo, useState } from 'react';

import { useQuery } from '@apollo/client/react';
import { Box, CircularProgress, Stack } from '@mui/material';
import type { GridRowId } from '@mui/x-data-grid-premium';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  type GetKanbanLeadsAgentsQueryVariables,
  type GetLeadsQueryVariables,
  type InputMaybe,
  type Lead_Agents_Bool_Exp,
  type LeadsKanbanColumnItemFragment,
  type Leads_Bool_Exp,
  Leads_Status_Enum,
} from '@graphql/__generated__/graphql';
import { CreateListingModal } from '@modules/_shared/components/create-listing/CreateListingModal';
import type { IndicatorStatus } from '@modules/_shared/components/IndicatorLeadStage';
import { KanbanBoard } from '@modules/_shared/components/kanban/KanbanBoard';
import { useUpdateLeadAgents } from '@modules/_shared/hooks/useUpdateLeadAgents';
import DialogUpdatePipeline from '@modules/leads/DialogUpdatePipeline';
import { useUpdateLeadsStageInCache } from '@modules/leads/kanbanCache';
import type { LeadsRowSelectionModel } from '@modules/leads/Leads';
import LeadsKanbanColumn from '@modules/leads/LeadsKanbanColumn';
import { GET_PIPELINE_STAGES } from '@modules/leads/leadsQueries';

export type LeadKanbanItem = LeadsKanbanColumnItemFragment['lead'] &
  Pick<
    LeadsKanbanColumnItemFragment,
    'predicted_win_date' | 'win_probability' | 'stage_id'
  > & {
    broker: LeadsKanbanColumnItemFragment['user'] | null;
    lead_id: string;
    lead_agents: Pick<
      LeadsKanbanColumnItemFragment,
      'claimed_at' | 'user_id' | 'source'
    >[];
  };

export type MenuAction = {
  type: 'stage';
};

export const PAGE_SIZE = 20;

type LeadsKanbanViewProps = {
  pipelineId: string | null;
  where: GetLeadsQueryVariables['where'];
  selectedRows: GridRowId[];
  setSelectedRows: (rows: LeadsRowSelectionModel) => void;
};

// Any non-null plain object is a valid Leads_Bool_Exp — Hasura accepts
// arbitrary column-name keys, not just _and/_or/_not. The guard exists only
// to filter out nulls / non-objects / arrays inside _and/_or item lists.
function isLeadsBoolExp(obj: unknown): obj is Leads_Bool_Exp {
  return obj != null && typeof obj === 'object' && !Array.isArray(obj);
}

export const transformLeadsWhereToLeadAgentsWhere = (
  where: InputMaybe<Leads_Bool_Exp> | undefined,
): Lead_Agents_Bool_Exp | undefined => {
  if (where == null) {
    return undefined;
  }

  const result: Lead_Agents_Bool_Exp = {};
  const lead: Record<string, any> = {};

  for (const [key, value] of Object.entries(where)) {
    if (key === '_and' && Array.isArray(value)) {
      // For _and, recurse (and filter) only if each item "looks" like a Leads_Bool_Exp.
      result._and = value
        .filter(isLeadsBoolExp)
        .map(transformLeadsWhereToLeadAgentsWhere)
        .filter((v): v is Lead_Agents_Bool_Exp => v != null);
    } else if (key === '_or' && Array.isArray(value)) {
      // For _or, recurse (and filter) only if each item "looks" like a Leads_Bool_Exp.
      result._or = value
        .filter(isLeadsBoolExp)
        .map(transformLeadsWhereToLeadAgentsWhere)
        .filter((v): v is Lead_Agents_Bool_Exp => v != null);
    } else if (key === '_not' && value) {
      // For _not, recurse only if value "looks" like a Leads_Bool_Exp; otherwise skip (so that the linter is happy).
      if (isLeadsBoolExp(value)) {
        result._not = transformLeadsWhereToLeadAgentsWhere(value);
      }
    } else if (key === 'lead_agents' && value) {
      // Merge lead_agents at root.
      Object.assign(result, value);
    } else {
      // All other keys go under "lead".
      lead[key] = value;
    }
  }

  if (Object.keys(lead).length > 0) {
    result.lead = lead;
  }

  return Object.keys(result).length > 0 ? result : undefined;
};

export const buildLeadsKanbanQueryVars = (
  includeLeads: boolean,
  where: InputMaybe<Leads_Bool_Exp> | undefined,
  stageIdToUse: string | null,
): GetKanbanLeadsAgentsQueryVariables => {
  const transformedWhere = includeLeads
    ? {}
    : transformLeadsWhereToLeadAgentsWhere(where);

  return {
    where: {
      _and: [
        transformedWhere ?? {},
        { lead_stage: { id: { _eq: stageIdToUse } } },
      ],
    },
    where_leads: {
      _and: [includeLeads ? where ?? {} : {}, { _not: { lead_agents: {} } }],
    },
    include_leads: includeLeads,
    limit: PAGE_SIZE,
    offset: 0,
  };
};

export const LeadsKanbanView = ({
  pipelineId,
  where,
  selectedRows,
  setSelectedRows,
}: LeadsKanbanViewProps) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const updateLeadsStage = useUpdateLeadsStageInCache();

  const [pipelineDialog, setPipelineDialog] = useState<{
    opened: boolean;
    lead?: { leadId: string; agentId: string; id: string };
  }>({ opened: false });

  const [createListingState, setCreateListingState] = useState<{
    open: boolean;
    fromLeadId: string;
    brokerId: string;
  }>({ open: false, fromLeadId: '', brokerId: '' });

  const closeCreateListing = useCallback(
    () => setCreateListingState({ open: false, fromLeadId: '', brokerId: '' }),
    [],
  );

  const { data: stagesData, loading: stagesLoading } = useQuery(
    GET_PIPELINE_STAGES,
    {
      variables: { pipeline_id: pipelineId ?? '' },
      skip: pipelineId == null,
    },
  );

  const stages = useMemo(
    () => stagesData?.pipelines_by_pk?.stages ?? [],
    [stagesData?.pipelines_by_pk?.stages],
  );

  const [columnItems, setColumnItems] = useState<
    Record<string, LeadKanbanItem[]>
  >({});

  // Get all leads across all columns
  const allLeads = useMemo(
    () => Object.values(columnItems).flat(),
    [columnItems],
  );

  const leadLink = useCallback(
    (id: string) => ({
      pathname: `/leads/${id}`,
      search: searchParams.toString(),
    }),
    [searchParams],
  );

  const handleLeadUpdate = useCallback(
    (leadId: string, action: MenuAction) => {
      if (action.type === 'stage') {
        const item = allLeads.find(lead => lead.id === leadId);
        if (item?.broker == null) {
          return;
        }

        setPipelineDialog({
          opened: true,
          lead: { leadId: item.lead_id, agentId: item.broker?.id, id: item.id },
        });
      }
    },
    [allLeads],
  );

  const updateColumnItems = useCallback(
    (columnId: string, items: LeadKanbanItem[]) => {
      setColumnItems(prev => {
        if (JSON.stringify(prev[columnId]) === JSON.stringify(items)) {
          return prev;
        }

        return {
          ...prev,
          [columnId]: items,
        };
      });
    },
    [],
  );

  const onItemsSelected = useCallback(
    (newSelection: string[]) => {
      const newSelectedRows = newSelection.map(row => ({
        id: row,
        availableOperations: ['UPDATE_PIPELINE' as const],
      }));

      setSelectedRows(newSelectedRows);
    },
    [setSelectedRows],
  );

  const [updateLeadAgent] = useUpdateLeadAgents();

  const columns = useMemo(() => {
    if (!stages.length) {
      return [];
    }

    return stages.map(stage => ({
      id: stage.id,
      data: {
        id: stage.id,
        label: stage.label ?? '',
        status: stage.status,
      },
      extractItems: (items: LeadKanbanItem[]) =>
        items.filter(
          item =>
            (stage.name === 'new' && item.stage_id == null) ||
            item.stage_id === stage.id,
        ),
      render: ({
        column,
        index,
      }: {
        column: { data: { label: string; id: string; status: string } };
        index: number;
      }) => (
        <LeadsKanbanColumn
          where={where}
          columnId={column.data.id}
          label={column.data.label}
          status={column.data.status as IndicatorStatus}
          onItemClick={(lead: LeadKanbanItem) =>
            navigate(leadLink(lead.lead_id))
          }
          index={index}
          onLeadUpdate={handleLeadUpdate}
          isFallbackColumn={stage.name === 'new'}
          onItemsChange={items => {
            updateColumnItems(column.data.id, items);
          }}
        />
      ),
    }));
  }, [stages, where, handleLeadUpdate, navigate, leadLink, updateColumnItems]);

  const onLeadMove = (itemId: string, stageId: string) => {
    const item = allLeads.find(lead => lead.id === itemId);

    // We cannot move an item with no stage.
    if (!item?.stage_id) {
      return;
    }

    // We don't do anything if the stage was not changed.
    if (item.stage_id === stageId) {
      return;
    }

    // Find the target stage
    const targetStage = stages.find(stage => stage.id === stageId);
    if (!targetStage) {
      return;
    }

    // If the lead has no lots and we're moving to a won stage, open the modal and skip the update
    if (
      item.lots?.length === 0 &&
      targetStage.status === Leads_Status_Enum.Won
    ) {
      setCreateListingState({
        open: true,
        fromLeadId: item.lead_id,
        brokerId: item.broker?.id ?? '',
      });
      return;
    }

    updateLeadsStage({
      stageId,
      leadAgentIds: [item.id],
      where,
    });

    updateLeadAgent(item.lead_id, { stage_id: stageId }, item.broker?.id);
  };

  if (stagesLoading) {
    return (
      <Stack
        sx={{
          flexGrow: 1,
          justifyContent: 'center',
          alignItems: 'center',
          p: 3,
        }}
      >
        <CircularProgress disableShrink />
      </Stack>
    );
  }

  return (
    <Box sx={{ flexGrow: 1, height: 0 }}>
      <KanbanBoard
        items={allLeads}
        selectedItems={selectedRows.map(id => String(id))}
        onItemsSelected={onItemsSelected}
        columns={columns}
        getItemId={(item: LeadKanbanItem) => item?.id ?? ''}
        onItemMoved={(item, columnId) => {
          if (item?.id) {
            onLeadMove(item.id, columnId);
          }
        }}
      />

      <DialogUpdatePipeline
        leadsIds={pipelineDialog.lead != null ? [pipelineDialog.lead] : []}
        pipeline="sales"
        initialPipelineId={pipelineId ?? ''}
        open={pipelineDialog.opened}
        onCancel={() => setPipelineDialog({ opened: false })}
        updateCacheFn={({ stageId }) => {
          if (pipelineDialog.lead != null) {
            updateLeadsStage({
              stageId,
              leadAgentIds: [pipelineDialog.lead.id],
              where,
            });
          }
        }}
      />

      <CreateListingModal
        open={createListingState.open}
        onClose={closeCreateListing}
        fromLeadId={createListingState.fromLeadId}
        brokerId={createListingState.brokerId || undefined}
        onListingCreated={() => Promise.resolve(closeCreateListing())}
      />
    </Box>
  );
};

export default LeadsKanbanView;
